/**
 * Claim Upload Receipt Modal Component
 */
import React, { useState, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Upload, FileText } from 'lucide-react';
import { fileUploadAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';

export const ClaimUploadModal = ({ 
  isOpen, 
  onClose, 
  claim, 
  onSuccess 
}) => {
  const fileInputRef = useRef(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !claim) return;
    
    try {
      setUploadingFile(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      await fileUploadAPI.uploadClaimReceipt(claim.id, formData);
      toast.success('Receipt uploaded successfully');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onClose();
      onSuccess();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to upload receipt');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  if (!claim) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Receipt</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="bg-slate-50 p-3 rounded-lg">
            <p className="font-medium">{claim.title}</p>
            <p className="text-lg font-bold">{formatCurrency(claim.amount)}</p>
          </div>
          
          <div>
            <Label>Select PDF Receipt</Label>
            <div 
              className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-slate-300 transition-colors mt-2"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
              {selectedFile ? (
                <p className="text-sm text-slate-700">{selectedFile.name}</p>
              ) : (
                <>
                  <p className="text-sm text-slate-500">Click to select PDF file</p>
                  <p className="text-xs text-slate-400 mt-1">Max 5MB</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleUpload} 
            disabled={!selectedFile || uploadingFile}
          >
            {uploadingFile ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClaimUploadModal;
